import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import classes from '../SearchResults.module.scss'
import { SearchResultsSection } from '../SearchResultsSection/SearchResultsSection'

import { useAuth, useRestrictedDonationConfig } from '@percent/workplace-giving/common/hooks'
import { OrganisationCardSkeleton } from '@percent/workplace-giving/common/components/OrganisationCard/OrganisationCardSkeleton'
import { OrganisationCard } from '@percent/workplace-giving/common/components/OrganisationCard/OrganisationCard'
import { useDonationForm } from '@percent/workplace-giving/common/hooks/useDonationForm/useDonationForm'
import { useColorTheme } from '@percent/workplace-giving/common/hooks/useColorTheme/useColorTheme'
import { RequestNonprofitPrompt } from '@percent/workplace-giving/app/RequestNonprofit/RequestNonprofitPrompt/RequestNonprofitPrompt'
import { AppRoute } from '@percent/workplace-giving/routing/AppRoute.enum'
import { formatNumberToLocale } from '@percent/workplace-giving/utils/formatNumberToLocale/formatNumberToLocale'
import {
  getCountryCodeFromAuthState,
  getStateCodeFromAuthState
} from '@percent/workplace-giving/context/auth/authContextController/AuthContextController'
import { VerifiedOrganisationSearchResult } from '@percent/workplace-giving/api/search/searchOrganisations/searchVerifiedOrganisations.types'
import { ListResponse } from '@percent/workplace-giving/api/goodstack'
import { BlockedRegionsConfig } from '@percent/workplace-giving/api/donation/getDonationPartnerConfig/getDonationPartnerConfig.types'

type OrganisationsResultsProps = Readonly<{
  data?: ListResponse<VerifiedOrganisationSearchResult>
  isFetching: boolean
  query?: string
  countryCode: string
}>

export function OrganisationsResults({ data, isFetching, query, countryCode }: OrganisationsResultsProps) {
  const {
    t,
    i18n: { language }
  } = useTranslation()
  const { theme } = useColorTheme()
  const { handleDonate, DonationFormWrapper } = useDonationForm('regular')
  const { state } = useAuth()
  const { blockedRegions } = useRestrictedDonationConfig()

  // Blocked-country regions gate the donate action on each card (passed to OrganisationCard
  // below); they do NOT hide orgs from the list. Country exclusion for the result set itself is
  // applied backend-side by the search API, so any org it returns is meant to be shown here.
  const blockedCountryCodes = useMemo(() => {
    return blockedRegions.map((region: BlockedRegionsConfig) => region.countryCode)
  }, [blockedRegions])

  const organisations = useMemo(() => data?.data ?? [], [data])

  const items = useMemo(
    () =>
      organisations.map(organisation => ({
        id: organisation.organisationId,
        content: (
          <OrganisationCard
            trackingOrigin="Search"
            key={organisation.organisationId}
            organisation={organisation}
            handleDonate={handleDonate}
            userOrigin={{
              country: getCountryCodeFromAuthState(state),
              state: getStateCodeFromAuthState(state)
            }}
            blockedCountryCodes={blockedCountryCodes}
          />
        )
      })),
    [organisations, handleDonate, state, blockedCountryCodes]
  )

  return (
    <div>
      <SearchResultsSection
        title={t('workplace_giving.globalSearch.searchResults.nonprofits', {
          totalResults: formatNumberToLocale(language, data?.totalResults || 0)
        })}
        noResultsText={t('workplace_giving.globalSearch.searchResults.noNonprofits')}
        totalResults={data?.totalResults || 0}
        isFetching={isFetching}
        items={items}
        skeleton={<OrganisationCardSkeleton />}
        seeAllTo={`${AppRoute.ORGANISATIONS}?query=${query}&country=${countryCode}`}
      >
        <div className={classes.RequestNonprofitWrapper}>
          <RequestNonprofitPrompt eventOrigin="Global Search Page" align="start" />
        </div>
      </SearchResultsSection>

      <DonationFormWrapper
        params={{
          primaryColor: theme.primary400
        }}
      />
    </div>
  )
}
